import { useEffect, useState, type RefObject } from 'react';

/**
 * An element's live content width, or 0 before it has been measured.
 *
 * Measured rather than derived from the viewport: a pane inside the app shell
 * shares the window with the nav dock and whatever detail panel is open, so it
 * is routinely much narrower than the screen.
 *
 * Every observed pixel is a render, so keep this on the component that needs
 * the number itself. A consumer that only wants a threshold ("is there room
 * for X?") should derive the boolean inside its own hook, where identical
 * values bail out of rendering.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setWidth(element.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}
